import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';
import type {
  ScheduleDefinition,
  SchedulesListResponse,
  CredentialStatus,
  CreateSchedulePayload,
  UpdateSchedulePayload,
  CredentialIssuePayload,
} from 'src/types/Schedules';
import { FetchResult } from 'src/types/TApiQueries';

const BASE = `${config.hostUrl}/api/v1/ai/schedules`;

export async function getSchedules(
  accessToken: string,
  status?: 'active' | 'paused' | 'all',
): Promise<FetchResult<SchedulesListResponse>> {
  const qs = status && status !== 'all' ? `?status=${status}` : '';
  return performFetchWithLogging<SchedulesListResponse>(`${BASE}${qs}`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getSchedule(
  accessToken: string,
  id: string,
): Promise<FetchResult<ScheduleDefinition>> {
  return performFetchWithLogging<ScheduleDefinition>(`${BASE}/${id}`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function createSchedule(
  accessToken: string,
  payload: CreateSchedulePayload,
): Promise<FetchResult<ScheduleDefinition>> {
  return performFetchWithLogging<ScheduleDefinition>(BASE, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
}

export async function updateSchedule(
  accessToken: string,
  id: string,
  payload: UpdateSchedulePayload,
): Promise<FetchResult<ScheduleDefinition>> {
  return performFetchWithLogging<ScheduleDefinition>(`${BASE}/${id}`, {
    method: 'PATCH',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
}

export async function deleteSchedule(
  accessToken: string,
  id: string,
): Promise<FetchResult<void>> {
  return performFetchWithLogging<void>(`${BASE}/${id}`, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getScheduleCredentials(
  accessToken: string,
): Promise<FetchResult<CredentialStatus>> {
  return performFetchWithLogging<CredentialStatus>(`${BASE}/credentials`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function issueScheduleCredential(
  accessToken: string,
  payload: CredentialIssuePayload,
): Promise<FetchResult<CredentialStatus>> {
  return performFetchWithLogging<CredentialStatus>(`${BASE}/credentials/issue`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
}

export async function revokeScheduleCredentials(
  accessToken: string,
): Promise<FetchResult<void>> {
  return performFetchWithLogging<void>(`${BASE}/credentials`, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}
